import { Form, Formik } from "formik";
import { FC, useContext, useEffect, useMemo, useRef, useState } from "react";

import nextStepIcon from "../../assets/arrowRightWhite.svg";
import goBackIcon from "../../assets/backIcon.svg";
import {
  clubTypeSteps,
  CREATE_CLUB_TOTAL_STEPS,
} from "../../common/constants/clubs-wizard.constants";
import {
  EMPTY_STRING,
  ENTER_KEY_CODE,
  MESSAGE_TYPE,
  SESSION_CLUB_KEY,
  TEXTAREA,
} from "../../common/constants/common.constants";
import {
  CLUB_DETAILS,
  HOME,
  PROJECT_FUNDRAISE,
  TYPE,
} from "../../common/constants/routes.constants";
import {
  CreateClubWizardSteps,
  GovernanceConfigurationType,
} from "../../common/enums/clubs-wizard.enum";
import { ICreateClubWizardForm } from "../../common/interfaces/clubs-wizard.interface";
import ClubBasicInfo from "../../components/CreateClubWizard/ClubBasicInfo/ClubBasicInfo";
import ClubMembers from "../../components/CreateClubWizard/ClubMembers/ClubMembers";
import ClubPreview from "../../components/CreateClubWizard/ClubPreview/ClubPreview";
import ClubRoles from "../../components/CreateClubWizard/ClubRoles/ClubRoles";
import FinancialConfiguration from "../../components/CreateClubWizard/FinancialConfiguration/FinancialConfiguration";
import GovernanceConfiguration from "../../components/CreateClubWizard/GovernanceConfiguration/GovernanceConfiguration";

import formModel from "./formConfig";

import "./CreateClubWizard.scss";

import { useAnchorWallet, useWallet } from "@solana/wallet-adapter-react";

import { validateClubCreationForm } from "./validators";

import { createNotification } from "../../utilities/notification";
import { createClubOnSolana } from "../../solana/methods/clubs";

import { useNavigate } from "react-router";
import NftConfiguration from "../../components/CreateClubWizard/NftConfiguration/NftConfiguration";

import FormButtons from "../../components/FormButtons/FormButtons";
import { UserRole } from "../../common/enums/common.enums";
import ErrorNotification from "../../components/ErrorNotification/ErrorNotification";
import TreasuryConfiguration from "../../components/CreateClubWizard/TreasuryConfiguration/TreasuryConfiguration";
import { useGateway } from "@civic/solana-gateway-react";
import MembershipVerificationStep from "../../components/CreateClubWizard/ClubMembers/MembershipRestriction/MembershipVerificationStep";
import { RestrictionOptions } from "../../common/enums/civic.enum";
import { useSearchParams } from "react-router-dom";
import PermissionDenied from "../../components/PermissionDenied/PermissionDenied";
import { checkIfUserCanCreateClub } from "../../solana/common/helpers/common.helpers";
import { UserContext } from "../../contexts/userContext";
import {
  governanceSeed,
  programFactory,
  splGovernanceProgram,
  unqClubSeed,
} from "../../solana/common/utilities";
import { web3 } from "@project-serum/anchor";
import { PublicKey } from "@solana/web3.js";

const CreateClubWizard: FC = () => {
  const { requestGatewayToken, gatewayToken } = useGateway();
  const [activeStep, setActiveStep] = useState<CreateClubWizardSteps>(
    CreateClubWizardSteps.BasicInfo
  );
  const { isWhitelisted, adminData } = useContext(UserContext);
  const { formId, formFields } = formModel;
  const wallet = useWallet();
  const anchorWallet = useAnchorWallet();
  const [isModifying, toggleModifying] = useState(false);
  const [sessionValues, setSessionValues] = useState();
  //TODO@milica-monke-merge: Only for monke
  const [shouldUserCreateClub, toggleShouldUserCreateClub] =
    useState<boolean>();
  const navigate = useNavigate();
  const [params] = useSearchParams();

  const wizardHeader = useRef<null | HTMLDivElement>(null);
  useEffect(() => {
    window.addEventListener("beforeunload", onBeforeUnload, true);
  }, [wallet.publicKey, CREATE_CLUB_TOTAL_STEPS]);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  useEffect(() => {
    //TODO@milica-monke-merge: Only for monke
    toggleShouldUserCreateClub(
      checkIfUserCanCreateClub(wallet?.publicKey, adminData)
    );
    if (!wallet.publicKey && sessionValues) {
      sessionStorage.setItem(SESSION_CLUB_KEY, JSON.stringify(sessionValues));
      return;
    }
    const loadedClubData = sessionStorage.getItem(SESSION_CLUB_KEY);

    if (wallet.publicKey && loadedClubData) {
      const sessionValues = JSON.parse(loadedClubData!);
      sessionValues.treasuryCapStart = new Date(sessionValues.treasuryCapStart);
      sessionValues.treasuryCapEnd = new Date(sessionValues.treasuryCapEnd);
      if (
        sessionValues.initialMembers[0].pubKey === wallet.publicKey!.toString()
      ) {
        setSessionValues(sessionValues);
      } else {
        sessionStorage.removeItem(SESSION_CLUB_KEY);
        setSessionValues(undefined);
        setActiveStep(CreateClubWizardSteps.BasicInfo);
      }
    }
  }, [anchorWallet?.publicKey, adminData]);

  const getAllSteps = (values) => {
    return clubTypeSteps.get(values.governance);
  };

  const findForwardStep = (values, forwardSteps = 1) => {
    const allSteps = getAllSteps(values);
    const currentStep = allSteps && allSteps.indexOf(activeStep!);
    return allSteps ? allSteps[Number(currentStep) + forwardSteps] : activeStep;
  };

  const findPreviousStep = (values, previousStep = 1) => {
    const allSteps = getAllSteps(values);
    const currentStep = allSteps && allSteps.indexOf(activeStep!);
    return allSteps ? allSteps[Number(currentStep) - previousStep] : activeStep;
  };

  const getVerificationWizard = (values) => {
    if (values.eligibleUsers === RestrictionOptions.VerifiedUsers) {
      return <MembershipVerificationStep />;
    } else {
      setActiveStep(findForwardStep(values));
    }
  };

  const initialValues = useMemo(
    () =>
      (() => {
        const values = {};
        Object.keys(formFields).map((key) => {
          if (key === formFields.initialMembers.name) {
            values[key] = [
              {
                pubKey: wallet?.publicKey?.toString(),
                role: UserRole.owner,
              },
            ];
          } else values[key] = formFields[key].initialValue;
        });
        return values;
      })(),
    [wallet.publicKey]
  );
  async function submitClubData(values: ICreateClubWizardForm) {
    try {
      const clubProgram = programFactory();
      const [realmAddress] = await web3.PublicKey.findProgramAddress(
        [governanceSeed, Buffer.from(values.clubName)],
        splGovernanceProgram
      );

      const [clubData] = await PublicKey.findProgramAddress(
        [unqClubSeed, realmAddress.toBuffer()],
        clubProgram.programId
      );

      // if (wallet.publicKey)
      //   await createClubData({
      //     clubAddress: clubData.toString(),
      //     founderPubkey: wallet.publicKey.toString(),
      //     clubConfig: JSON.stringify(values),
      //     updatedAt: new Date(),
      //     createdAt: new Date(),
      //   });
    } catch (error: any) {
      console.log(error);
    }
  }

  async function submitForm(values: ICreateClubWizardForm, actions) {
    try {
      const { clubAddress } = await createClubOnSolana(
        values,
        anchorWallet!,
        undefined,
        false,
        [],
        goToClubDetails
      );
      navigate(`${CLUB_DETAILS}/${clubAddress}`);
      sessionStorage.removeItem(SESSION_CLUB_KEY);
      setActiveStep(findForwardStep(values));
    } catch (error: any) {
      console.log(error);
      createNotification(
        MESSAGE_TYPE.ERROR,
        "Club creation failed",
        error.message
      );
    } finally {
      actions.setSubmitting(false);
    }
  }
  async function handleSubmit(values, actions) {
    setSessionValues(values);
    submitClubData(values);

    if (activeStep === CreateClubWizardSteps.Preview) {
      void submitForm(values, actions);
    } else {
      wizardHeader.current &&
        window.scrollTo(wizardHeader.current.offsetTop, 0);

      if (
        values.eligibleUsers === RestrictionOptions.VerifiedUsers &&
        isVerificationStep()
      ) {
        if (gatewayToken) {
          setActiveStep(findForwardStep(values, 2));
          actions.setSubmitting(false);
        } else {
          await requestGatewayToken();
          return;
        }
      }

      if (
        activeStep === CreateClubWizardSteps.BasicInfo &&
        params.get(TYPE) === PROJECT_FUNDRAISE
      ) {
        setActiveStep(CreateClubWizardSteps.Roles);
        values.governance = GovernanceConfigurationType.ProjectFundraise;
        values.investor = true;
        values.member = false;
        return;
      }

      setActiveStep(findForwardStep(values));
      actions.setSubmitting(false);
    }
  }

  const onBeforeUnload = (event) => {
    if (window.location.href.includes("create-club") && wallet.connected) {
      sessionStorage.removeItem(SESSION_CLUB_KEY);
      event.returnValue = true;
    }
  };

  const goToClubDetails = (clubAddress) => {
    navigate(`${CLUB_DETAILS}/${clubAddress}`);
  };

  async function handleBack(values, errors, setErrors) {
    setErrors({});

    if (isModifying) {
      setSessionValues(values);
    }

    if (isModifying && !isLastStep() && errors) {
      if (Object.keys(errors).length === 0) {
        setActiveStep(CreateClubWizardSteps.Preview);
        return;
      } else {
        return;
      }
    }

    if (activeStep === CreateClubWizardSteps.BasicInfo) {
      navigate(HOME);
      return;
    }

    if (
      activeStep === CreateClubWizardSteps.FundraisingConfiguration &&
      values.eligibleUsers === RestrictionOptions.AllUsers
    ) {
      setActiveStep(findPreviousStep(values, 2));
      return;
    }
    if (
      params.get(TYPE) === PROJECT_FUNDRAISE &&
      activeStep === CreateClubWizardSteps.Roles
    ) {
      setActiveStep(CreateClubWizardSteps.BasicInfo);
      return;
    }
    setActiveStep(findPreviousStep(values));
    toggleModifying(false);
  }

  /**
   *  Prevent default action on enter key in Create Club Wizard
   * @param keyEvent {any}
   */
  function onKeyDown(keyEvent) {
    if (
      (keyEvent.charCode || keyEvent.keyCode) === ENTER_KEY_CODE &&
      keyEvent.target.nodeName !== TEXTAREA
    ) {
      keyEvent.preventDefault();
    }
  }

  function changeActiveStep(step: CreateClubWizardSteps) {
    toggleModifying(true);
    setActiveStep(step);
  }

  function renderStepContent(
    step,
    values,
    setValues,
    setFieldValue,
    setSubmitting
  ) {
    if (
      values === initialValues &&
      sessionValues &&
      sessionValues["initialMembers"][0]["pubKey"] ===
        wallet.publicKey?.toString()
    ) {
      setValues(sessionValues);
    }

    switch (step) {
      case CreateClubWizardSteps.BasicInfo:
        return (
          <ClubBasicInfo
            formFields={formModel.formFields}
            setFieldValue={setFieldValue}
            setSubmitting={setSubmitting}
            values={values}
          />
        );
      case CreateClubWizardSteps.GovernanceConfiguration:
        return <GovernanceConfiguration />;
      case CreateClubWizardSteps.NFTConfiguration:
        return <NftConfiguration formFields={formFields} />;
      case CreateClubWizardSteps.Roles:
        return <ClubRoles formFields={formFields} />;
      case CreateClubWizardSteps.Members:
        return <ClubMembers formFields={formFields} />;
      case CreateClubWizardSteps.Verification:
        return getVerificationWizard(values);
      case CreateClubWizardSteps.FundraisingConfiguration:
        return <TreasuryConfiguration form={formModel} />;
      case CreateClubWizardSteps.FinancialConfiguration:
        return <FinancialConfiguration formFields={formFields} />;
      case CreateClubWizardSteps.Preview:
        return <ClubPreview jumpToSection={changeActiveStep} />;
      default:
        return <div>Not Found</div>;
    }
  }
  const setUpWizardHeader = (values) => {
    if (!activeStep) {
      return EMPTY_STRING;
    }

    switch (activeStep) {
      case CreateClubWizardSteps.BasicInfo:
        return params.get(TYPE) === PROJECT_FUNDRAISE
          ? "Start your project fundraise"
          : "Start your Club";
      case CreateClubWizardSteps.GovernanceConfiguration:
        return "Choose Club’s Governance";
      case CreateClubWizardSteps.NFTConfiguration:
        return "Collection Configuration";
      case CreateClubWizardSteps.Roles:
        return values.governance === GovernanceConfigurationType.RoleBased
          ? "Choose and Configure Club Roles"
          : "Choose Club’s Roles";
      case CreateClubWizardSteps.Members:
        return "Invite Club Members";
      case CreateClubWizardSteps.FundraisingConfiguration:
        return "Treasury Configuration";
      case CreateClubWizardSteps.FinancialConfiguration:
        return "Proposal Voting Configuration";
      case CreateClubWizardSteps.Preview:
        return "Club Configuration Preview";
      default:
        return EMPTY_STRING;
    }
  };

  const isLastStep = () => activeStep === CreateClubWizardSteps.Preview;

  const isClubBasicInfoStep = () =>
    activeStep === CreateClubWizardSteps.BasicInfo;

  const isVerificationStep = () =>
    activeStep === CreateClubWizardSteps.Verification;

  const isPenulimitedStep = (values) =>
    findForwardStep(values) === CreateClubWizardSteps.Preview ? true : false;

  const isFundraisingStepAndNotSkippedFundraise = (values) => {
    return (
      activeStep === CreateClubWizardSteps.FundraisingConfiguration &&
      !values.skipFundraise
    );
  };

  const nextButtonLabel = (values) => {
    return isPenulimitedStep(values)
      ? "Preview club"
      : isLastStep()
      ? "Create club"
      : activeStep === CreateClubWizardSteps.Verification && !gatewayToken
      ? "Proceed to verify"
      : "Next step";
  };

  const backButtonLabel = () => {
    return isClubBasicInfoStep() && !isModifying
      ? "Cancel creation"
      : isModifying && !isLastStep()
      ? "Back to preview"
      : "Go back";
  };
  //TODO@milica-monke-merge: Permissions are only for monke
  return (
    <div className="create-club">
      {shouldUserCreateClub ? (
        <div className="create-club__content" ref={wizardHeader}>
          {!wallet.publicKey ? (
            <div>
              <h3 className="create-club__label">Please connect your wallet</h3>
            </div>
          ) : (
            <Formik
              initialValues={initialValues}
              onSubmit={handleSubmit}
              validateOnChange
              validate={(values) =>
                validateClubCreationForm(values, activeStep)
              }
            >
              {({
                isSubmitting,
                setFieldValue,
                values,
                setValues,
                errors,
                setSubmitting,
                touched,
                setErrors,
              }) => (
                <Form id={formId} onKeyDown={onKeyDown}>
                  <h3 className="create-club__label">
                    {setUpWizardHeader(values)}
                  </h3>
                  {renderStepContent(
                    activeStep,
                    values,
                    setValues,
                    setFieldValue,
                    setSubmitting
                  )}
                  <FormButtons
                    submitting={isSubmitting}
                    onCancel={() => handleBack(values, errors, setErrors)}
                    onSubmit={() => {
                      if (isFundraisingStepAndNotSkippedFundraise(values))
                        setFieldValue("skipFundraise", false);
                    }}
                    typeSubmit="submit"
                    cancelLabel={backButtonLabel()}
                    submitLabel={nextButtonLabel(values)}
                    disabled={isSubmitting}
                    typeCancel={
                      isModifying && !isLastStep() ? "submit" : "button"
                    }
                    cancelIcon={
                      (isClubBasicInfoStep() && isModifying) ||
                      !isClubBasicInfoStep()
                        ? goBackIcon
                        : undefined
                    }
                    submitIcon={!isLastStep() ? nextStepIcon : ""}
                  />
                  {errors && (
                    <ErrorNotification
                      errors={errors}
                      isTouched={touched}
                      arrayKey="initialMembers"
                      secondArrayKey="votingThreshold"
                    />
                  )}
                </Form>
              )}
            </Formik>
          )}
        </div>
      ) : (
        <PermissionDenied
          message={"You dont have permission to view this page"}
        />
      )}
    </div>
  );
};

export default CreateClubWizard;
